import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { resetTheme, updateThemeLocal } from '../store/themeSlice';
import BrandingService from '../utils/brandingService';

import apiClient from '../utils/apiClient';

export const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [tenantKey, setTenantKey] = useState(0);
  const [isBrandingLoaded, setIsBrandingLoaded] = useState(false);
  const [brandingData, setBrandingData] = useState(null);
  const dispatch = useDispatch();

  const fetchBrandingData = useCallback(async () => {
    try {
      const res = await apiClient.get('/branding');
      const { BrandingTheme, Picture } = res.data || {};
      
      setBrandingData({ BrandingTheme, Picture });
      
      if (BrandingTheme && BrandingTheme.TenantId) {
        const themePayload = {
          'color-primary': BrandingTheme.ColorPrimary,
          'color-secondary': BrandingTheme.ColorSecondary,
          'color-bg': BrandingTheme.ColorBg,
          'color-sidebar-bg': BrandingTheme.ColorSidebarBg,
          'color-text': BrandingTheme.ColorText,
          'color-landing-left': BrandingTheme.ColorLandingLeft,
          'color-landing-right': BrandingTheme.ColorLandingRight,
        };

        dispatch(updateThemeLocal(themePayload));
        BrandingService.setBrandingTheme(BrandingTheme);
      }

      if (Picture) {
        BrandingService.setImage(Picture);
      } else {
        BrandingService.clearImage();
      }
      
      return { BrandingTheme, Picture };
    } catch (e) {
      console.error('fetchBrandingData failed', e);
      return null;
    }
  }, [dispatch]);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/myadminprofile');
      setProfile(data);

      // Clear any cached image URLs since presigned URLs expire
      BrandingService.clearImage();

      const cachedTheme = BrandingService.getBrandingTheme();

      if (cachedTheme) {
        const themePayload = {
          'color-primary': cachedTheme.ColorPrimary,
          'color-secondary': cachedTheme.ColorSecondary,
          'color-bg': cachedTheme.ColorBg,
          'color-sidebar-bg': cachedTheme.ColorSidebarBg,
          'color-text': cachedTheme.ColorText,
          'color-landing-left': cachedTheme.ColorLandingLeft,
          'color-landing-right': cachedTheme.ColorLandingRight,
        };

        dispatch(updateThemeLocal(themePayload));
      }

      // Fetch fresh branding data (including new presigned URL)
      await fetchBrandingData();

      setTenantKey(k => k + 1);
      return data;
    } catch (e) {
      console.error('fetchProfile failed', e);
    } finally {
      setIsBrandingLoaded(true);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <ProfileContext.Provider value={{
      profile,
      refreshProfile: fetchProfile,
      setProfile,
      tenantKey,
      isBrandingLoaded,
      brandingData,
      refreshBranding: fetchBrandingData
    }}>
      {isBrandingLoaded ? children : (
        <div className="loader">
          <div className="loader-spinner"></div>
        </div>
      )}
    </ProfileContext.Provider>
  );
};
